import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {Platform, StyleSheet, Text, View, Keyboard} from 'react-native';
import {useSelector, useDispatch} from 'react-redux';
import {GiftedChat, InputToolbar, Send} from 'react-native-gifted-chat';
import emojiUtils from 'emoji-utils';
import Message from 'components/Communication/Message';
import {sendMessage} from 'store/communication/actions/chatActions';
import {I18nContext} from 'i18n/context/I18nContext';
import TranslationSwitcher from 'components/Communication/TranslationSwitcher';

// See https://github.com/FaridSafi/react-native-gifted-chat/issues/680
const ANDROID_KEYBOARD_OFFSET = 80;
const IOS_KEYBOARD_OFFSET = 0;
const COMPOSER_HEIGHT = 70;
const INPUT_TOOLBAR_HEIGHT = 80;
const SUBMIT_KEY = 'Enter';

const styles = StyleSheet.create({
  button: {
    marginTop: 'auto',
    marginBottom: 'auto',
  },
  container: {
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'center',
    borderColor: '#aaaaaa',
    borderWidth: 2,
    borderRadius: 3,
    flex: 0.5,
    backgroundColor: '#4bc236',
  },
  sendText: {
    color: '#ffffff',
    fontWeight: '600',
    fontSize: 17,
    backgroundColor: 'transparent',
    marginLeft: 10,
    marginRight: 10,
  },
});

const Messages = () => {
  const dispatch = useDispatch();
  const giftedChatInputRef = useRef();
  const renderCountRef = useRef(0);
  const {translate} = useContext(I18nContext);
  const chatMessages = useSelector((state) => state.communication.chat);
  const currentUser = useSelector((state) => state.communication.me);
  const isRoomChatMuted = useSelector(
    (state) => state.communication.room.muted.chat,
  );
  const isMeChatMuted = useSelector(
    (state) => state.communication.me.muted.chat,
  );
  const isChatMuted = isRoomChatMuted || isMeChatMuted;

  // Fix B: wrap render fns in useCallback to avoid busting GiftedChat's useMemo
  const renderMessage = useCallback((props) => {
    const {
      currentMessage: {text: currentText},
    } = props;

    let messageTextStyle;

    // Make "pure emoji" messages much bigger than plain text.
    if (currentText && emojiUtils.isPureEmojiString(currentText)) {
      messageTextStyle = {
        fontSize: 28,
        // Emoji get clipped if lineHeight isn't increased; make it consistent across platforms.
        lineHeight: Platform.OS === 'android' ? 34 : 30,
      };
    }

    return <Message {...props} messageTextStyle={messageTextStyle} />;
  }, []);

  // Fix A: spread props.textInputProps first to preserve GiftedChat's onChangeText + ref
  // Fix B: useCallback for stable identity
  // Fix C: ref removed from here — passed via textInputRef root prop instead
  const renderInputToolbar = useCallback((props) => {
    return (
      <InputToolbar
        {...props}
        primaryStyle={styles.inputBar}
        textInputProps={{
          ...props.textInputProps,
          onKeyPress: (e) => {
            if (
              e.key === SUBMIT_KEY &&
              !e.shiftKey &&
              props.onSend &&
              props.text
            ) {
              props.onSend({text: props.text.trim()}, true);
            }
          },
          blurOnSubmit: false,
          autoFocus: true,
          multiline: true,
        }}
      />
    );
  }, []);

  // Fix B: useCallback for stable identity
  const renderSend = useCallback((props) => {
    return (
      <Send
        {...props}
        containerStyle={[
          styles.container,
          {height: props.minInputToolbarHeight},
        ]}
        textStyle={styles.button}>
        <Text style={styles.sendText}>Send</Text>
      </Send>
    );
  }, []);

  renderCountRef.current += 1;

  // Manual keyboard tracking — bulletproof pattern used by every production
  // chat. iOS's automatic keyboard handling doesn't play nice with the
  // GiftedChat + side-panel + Unity background stack, so we measure the
  // keyboard height ourselves and lift the chat container with paddingBottom.
  // Tap-to-dismiss: GiftedChat's FlatList default keyboardShouldPersistTaps
  // is 'never', so a tap on the messages list dismisses the keyboard for us.
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  useEffect(() => {
    const showEvent =
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent =
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const showSub = Keyboard.addListener(showEvent, (e) => {
      setKeyboardHeight(e.endCoordinates.height);
    });
    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  return (
    <View style={{flex: 1, paddingBottom: keyboardHeight}}>
      <TranslationSwitcher />
      <GiftedChat
        messages={chatMessages}
        onSend={(messages) => {
          dispatch(sendMessage(messages[0]));
          giftedChatInputRef.current?.focus();
        }}
        user={{
          _id: currentUser.id,
          name: currentUser.displayName,
          avatar: currentUser.photoURL,
        }}
        renderMessage={renderMessage}
        textInputRef={giftedChatInputRef}
        textInputProps={{
          placeholder: isChatMuted
            ? translate('messages.chatDisabled')
            : translate('messages.sendMsgAll'),
          editable: !isChatMuted,
        }}
        isAvatarOnTop
        renderInputToolbar={renderInputToolbar}
        maxComposerHeight={COMPOSER_HEIGHT}
        minComposerHeight={COMPOSER_HEIGHT}
        minInputToolbarHeight={INPUT_TOOLBAR_HEIGHT}
        renderSend={renderSend}
        isKeyboardInternallyHandled={false}
        bottomOffset={0}
      />
    </View>
  );
};

export default Messages;
